'use client'

import { useRef, useState, useCallback, useEffect } from 'react'
import type { CSSProperties } from 'react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { getLenis } from '../hooks/useSmoothScroll'
import { openCV } from '../store/cvStore'
import { track } from '../lib/analytics'

export const NAV_ITEMS = [
  { label: 'Void',    target: '#void',    num: '01', sub: 'intro',      desc: 'Boot sequence — signal acquired' },
  { label: 'Signal',  target: '#signal',  num: '02', sub: 'about',      desc: 'Identity — who is behind the machine' },
  { label: 'Arsenal', target: '#arsenal', num: '03', sub: 'skills',     desc: 'Toolchain — the stack & capabilities' },
  { label: 'Path',    target: '#path',    num: '04', sub: 'experience', desc: 'Trajectory — the experience log' },
  { label: 'Gallery', target: '#gallery', num: '05', sub: 'work',       desc: 'Forge line — selected work' },
  { label: 'Proof',   target: '#proof',   num: '06', sub: 'recognition',desc: 'Recognition — records verified' },
  { label: 'Voices',  target: '#voices',  num: '07', sub: 'voices',     desc: 'Transmissions — received signals' },
  { label: 'Portal',  target: '#portal',  num: '08', sub: 'contact',    desc: 'Uplink — open a channel' },
  { label: 'End',     target: '#end',     num: '09', sub: 'farewell',   desc: 'Shutdown — transmission complete' },
]

const SECTION_IDS = NAV_ITEMS.map((i) => i.target.slice(1))

// What the particle FIELD is doing in each chapter — one word that makes the
// spine legible as a single authored system (and survives on mobile, where
// the WebGL field is off but the narrative continues in each section header).
// Exported so the right-rail spine (ScrollProgress) reads from the same source.
export const FIELD_VERBS: Record<string, string> = {
  void: 'locking', signal: 'resolving', arsenal: 'forging', path: 'tracing',
  gallery: 'reforging', proof: 'verifying', voices: 'receiving', portal: 'uplink', end: 'shutdown',
}

/* ─── HUD chrome (mirrors Gallery's FORGE TERMINAL primitives) ─── */
const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'

function HudCorners({ size = 16 }: { size?: number }) {
  const c = 'var(--color-fire)'
  const b: CSSProperties = { position: 'absolute', width: size, height: size, opacity: 0.5 }
  return (
    <>
      <span aria-hidden style={{ ...b, top: 0, left: 0, borderTop: `1px solid ${c}`, borderLeft: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, top: 0, right: 0, borderTop: `1px solid ${c}`, borderRight: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, left: 0, borderBottom: `1px solid ${c}`, borderLeft: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, right: 0, borderBottom: `1px solid ${c}`, borderRight: `1px solid ${c}` }} />
    </>
  )
}

function HexDot({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 9, height: 10, flexShrink: 0, alignSelf: 'center', clipPath: HEX_DOT,
        background: on ? 'var(--color-fire)' : 'var(--color-steel-deep)',
        boxShadow: on ? '0 0 9px var(--color-fire)' : 'none',
        transform: on ? 'scale(1)' : 'scale(0.78)',
        transition: 'all .4s var(--ease-out-quart)',
      }}
    />
  )
}

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('void')
  const [focusIdx, setFocusIdx] = useState(0) // which node the READOUT is previewing
  const overlayRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([])
  const tlRef = useRef<gsap.core.Timeline | null>(null)

  // Pick the section nearest the viewport's reading line. Returns the id so the menu
  // can seed its readout the instant it opens (not just on the next scroll).
  const computeActive = useCallback(() => {
    const vh = window.innerHeight
    const center = vh * 0.4
    let best = 'void'
    let bestDist = Infinity
    for (const id of SECTION_IDS) {
      const el = document.getElementById(id)
      if (!el) continue
      const parent = el.parentElement
      const target = parent?.classList.contains('pin-spacer') ? parent : el
      const rect = target.getBoundingClientRect()
      if (rect.top < vh && rect.bottom > 0) {
        const mid = (rect.top + rect.bottom) / 2
        const dist = Math.abs(mid - center)
        if (dist < bestDist) { bestDist = dist; best = id }
      }
    }
    setActiveSection((prev) => (prev === best ? prev : best))
    return best
  }, [])

  useEffect(() => {
    let raf = 0
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(computeActive) }
    window.addEventListener('scroll', onScroll, { passive: true })
    computeActive()
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf) }
  }, [computeActive])

  const { contextSafe } = useGSAP({ scope: overlayRef })

  const openMenu = contextSafe(() => {
    const activeId = computeActive()
    const idx = Math.max(0, SECTION_IDS.indexOf(activeId))
    setFocusIdx(idx)
    setIsOpen(true)
    const overlay = overlayRef.current
    if (!overlay) return
    if (tlRef.current) tlRef.current.kill()
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Reset any inline transform/opacity left by a killed (rapid toggle) tween.
    gsap.set(overlay.querySelectorAll('.nav-statusbar, .nav-link, .nav-seam, .nav-readout, .nav-info'), { clearProps: 'opacity,transform' })
    const tl = gsap.timeline({ onComplete: () => rowRefs.current[idx]?.focus() })
    tlRef.current = tl
    tl.set(overlay, { display: 'flex', flexDirection: 'column' })
    if (reduce) {
      tl.fromTo(overlay, { opacity: 0, clipPath: 'inset(0 0 0% 0)' }, { opacity: 1, duration: 0.2 })
      return
    }
    tl.fromTo(overlay, { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 0.6, ease: 'power4.inOut' })
    tl.from(overlay.querySelector('.nav-statusbar'), { y: -8, opacity: 0, duration: 0.3, ease: 'power2.out', clearProps: 'opacity,transform' }, '-=0.4')
    tl.from(overlay.querySelectorAll('.nav-link'), { y: 24, opacity: 0, stagger: 0.045, duration: 0.45, ease: 'power3.out', clearProps: 'opacity,transform' }, '-=0.28')
    tl.from(overlay.querySelector('.nav-seam'), { scaleY: 0, transformOrigin: 'top center', opacity: 0, duration: 0.5, ease: 'expo.out', clearProps: 'opacity,transform' }, '-=0.4')
    tl.from(overlay.querySelector('.nav-readout'), { x: 22, opacity: 0, duration: 0.5, ease: 'power3.out', clearProps: 'opacity,transform' }, '-=0.4')
    tl.from(overlay.querySelectorAll('.nav-info'), { y: 10, opacity: 0, stagger: 0.05, duration: 0.3, ease: 'power2.out', clearProps: 'opacity,transform' }, '-=0.2')
  })

  const closeMenu = contextSafe(() => {
    const overlay = overlayRef.current
    if (!overlay) return
    if (tlRef.current) tlRef.current.kill()
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const tl = gsap.timeline({ onComplete: () => { setIsOpen(false); toggleRef.current?.focus() } })
    tlRef.current = tl
    if (reduce) tl.to(overlay, { opacity: 0, duration: 0.2 })
    else tl.to(overlay, { clipPath: 'inset(100% 0 0 0)', duration: 0.5, ease: 'power4.inOut' })
    tl.set(overlay, { display: 'none', clipPath: 'inset(0 0 100% 0)', opacity: 1 })
  })

  // Esc closes while open
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, closeMenu])

  const navigateTo = useCallback(
    (target: string) => {
      closeMenu()
      setTimeout(() => {
        const lenis = getLenis()
        const el = document.querySelector(target)
        if (el && lenis) lenis.scrollTo(el as HTMLElement, { offset: 0, duration: 1.5 })
        else if (el) el.scrollIntoView({ behavior: 'smooth' })
      }, 650)
    },
    [closeMenu],
  )

  // Close the console first, then open the dossier once the wipe has finished —
  // two full-screen layers animating over each other reads as a glitch.
  const openDossier = useCallback(() => {
    closeMenu()
    track('cv_download', { method: 'nav_menu' })
    setTimeout(openCV, 520)
  }, [closeMenu])

  // Arrow keys walk the index (roving focus); Enter/Space navigate natively.
  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const next = e.key === 'ArrowDown'
      ? Math.min(NAV_ITEMS.length - 1, focusIdx + 1)
      : Math.max(0, focusIdx - 1)
    setFocusIdx(next)
    rowRefs.current[next]?.focus()
  }

  const fieldVerb = FIELD_VERBS[activeSection] ?? ''

  const activeItem = NAV_ITEMS.find((i) => i.target === `#${activeSection}`)
  const activeIdx  = Math.max(0, SECTION_IDS.indexOf(activeSection))
  const focus      = NAV_ITEMS[focusIdx] ?? NAV_ITEMS[0]
  const telemetry  = focusIdx < activeIdx ? '✓ PASSED' : focusIdx === activeIdx ? '◉ ACTIVE' : '◇ AHEAD'
  const telemetryColor = focusIdx === activeIdx ? 'var(--color-fire)' : focusIdx < activeIdx ? 'var(--color-mist)' : 'var(--color-stone)'
  const meterPct = (focusIdx / (NAV_ITEMS.length - 1)) * 100

  return (
    <>
      {/* ── Top bar ── */}
      <header
        className="fixed top-0 left-0 w-full z-100 flex items-center justify-between pointer-events-none"
        style={{ padding: 'clamp(1rem, 2.5vw, 1.5rem) clamp(1.5rem, 5vw, 5rem)' }}
      >
        {/* Logo */}
        <a
          href="#void"
          className="pointer-events-auto font-display text-xl lg:text-2xl font-bold tracking-tight opacity-90 hover:opacity-100 transition-opacity duration-300"
          data-cursor-label="Home"
          onClick={(e) => {
            e.preventDefault()
            if (isOpen) closeMenu()
            const lenis = getLenis()
            if (lenis) lenis.scrollTo(0, { duration: 2 })
            else window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        >
          <span style={{ color: 'var(--color-light)' }}>abd</span>
          <span style={{ color: 'var(--color-fire)' }}>orange</span>
        </a>

        {/* Active section indicator */}
        {activeItem && (
          <div className="pointer-events-none hidden sm:flex items-center gap-2 select-none" aria-hidden="true">
            {/* Heartbeat — reuses the ONLINE dot treatment; auto-calmed by the
                global prefers-reduced-motion rule in globals.css */}
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--color-fire)', boxShadow: '0 0 6px var(--color-fire)', animation: 'dot-pulse 2.4s ease-in-out infinite' }} />
            <span className="font-mono text-[10px] tracking-[0.2em]" style={{ color: 'var(--color-fire)' }}>{activeItem.num}</span>
            <svg width="6" height="6" viewBox="0 0 100 100" fill="none" style={{ opacity: 0.5 }}>
              <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" stroke="var(--color-fire)" strokeWidth="12" fill="none" />
            </svg>
            <span className="font-mono text-[10px] tracking-[0.15em] uppercase" style={{ color: 'var(--color-mist)' }}>{activeItem.label}</span>
            {fieldVerb && (
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: 'var(--color-stone)' }}>
                &middot; {fieldVerb}
              </span>
            )}
          </div>
        )}

        {/* Menu toggle */}
        <button
          ref={toggleRef}
          onClick={isOpen ? closeMenu : openMenu}
          className="pointer-events-auto flex items-center gap-3 group"
          style={{ padding: '15px 11px', margin: '-15px -11px' }}
          data-cursor-label={isOpen ? 'Close' : 'Menu'}
          aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
        >
          <span
            className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 select-none hidden sm:block"
            style={{ color: isOpen ? 'var(--color-fire)' : 'var(--color-fog)' }}
          >
            {isOpen ? 'close' : 'menu'}
          </span>
          {/* 3-bar icon → X (span — <button> only allows phrasing content) */}
          <span className="relative" style={{ display: 'inline-block', width: 22, height: 14 }}>
            <span className="absolute left-0 w-full h-[1.5px] transition-all duration-450"
              style={{ top: 0, backgroundColor: 'var(--color-light)', transform: isOpen ? 'translateY(6.25px) rotate(45deg)' : 'none' }} />
            <span className="absolute left-0 h-[1.5px] transition-all duration-300"
              style={{ top: '50%', marginTop: -0.75, width: '70%', backgroundColor: 'var(--color-fire)',
                opacity: isOpen ? 0 : 1, transform: isOpen ? 'scaleX(0)' : 'scaleX(1)', transformOrigin: 'left' }} />
            <span className="absolute left-0 w-full h-[1.5px] transition-all duration-450"
              style={{ bottom: 0, backgroundColor: 'var(--color-light)', transform: isOpen ? 'translateY(-6.25px) rotate(-45deg)' : 'none' }} />
          </span>
        </button>
      </header>

      {/* ── Fullscreen overlay: SYSTEM.MAP node console ── */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-99 overflow-hidden"
        style={{ display: 'none', backgroundColor: 'var(--color-void)', clipPath: 'inset(0 0 100% 0)', flexDirection: 'column' }}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        {/* Status bar */}
        <div className="nav-statusbar font-mono">
          <span style={{ color: 'var(--color-fog)' }}>[ NAV ]</span>
          <span style={{ color: 'var(--color-stone)' }}>⬡</span>
          <span style={{ color: 'var(--color-mist)' }}>SYSTEM.MAP</span>
          <span style={{ color: 'var(--color-ash)' }}>·</span>
          <span style={{ color: 'var(--color-stone)' }}>09 NODES</span>
          <span style={{ color: 'var(--color-ash)' }}>·</span>
          <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--color-fog)' }}>
            <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-fire)', animation: 'dot-pulse 2.4s ease-in-out infinite' }} />
            ONLINE
          </span>
          <span style={{ color: 'var(--color-ash)' }}>·</span>
          <span style={{ color: 'var(--color-fire)' }}>{String(focusIdx + 1).padStart(2, '0')}/09</span>
        </div>

        {/* Body grid: INDEX | seam | READOUT */}
        <div className="nav-body">
          {/* LEFT — the index (the real navigation) */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <nav className="nav-list" aria-label="Sections" onKeyDown={onListKey} onMouseLeave={() => setFocusIdx(activeIdx)}>
            <div className="nav-caption">SELECT_NODE //</div>
            {NAV_ITEMS.map((item, i) => {
              const isActive = i === activeIdx
              const isFocus = i === focusIdx
              return (
                <button
                  key={item.label}
                  ref={(el) => { rowRefs.current[i] = el }}
                  onClick={() => navigateTo(item.target)}
                  onMouseEnter={() => setFocusIdx(i)}
                  onFocus={() => setFocusIdx(i)}
                  className={`nav-link${isActive ? ' is-active' : ''}${isFocus ? ' is-focus' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                  data-cursor-label={item.num}
                >
                  <HexDot on={isActive || isFocus} />
                  <span className="nav-num font-mono tabular-nums">{item.num}</span>
                  <span className="nav-label font-display font-bold">{item.label}</span>
                  <span className="nav-sub font-mono">/ {item.sub}</span>
                  <span className="nav-caret font-mono" aria-hidden>◂</span>
                  <span className="nav-sep" aria-hidden />
                </button>
              )
            })}
          </nav>

          {/* CENTER — hex seam */}
          <div className="nav-seam" aria-hidden="true">
            <span className="seam-hex" style={{ top: '12%' }}>⬡</span>
            <span className="seam-hex" style={{ top: '50%', color: 'var(--color-fire)', opacity: 0.7 }}>⬡</span>
            <span className="seam-hex" style={{ top: '88%' }}>⬡</span>
          </div>

          {/* RIGHT — node readout (fills the old dead space) */}
          <div className="nav-readout" aria-hidden="true">
            <HudCorners size={16} />
            <span className="nav-caption readout-cap">// READOUT</span>
            <span key={`wm-${focusIdx}`} className="readout-watermark font-display tabular-nums">{focus.num}</span>
            <div key={`body-${focusIdx}`} className="readout-body">
              <div className="readout-node font-mono">NODE {focus.num}</div>
              <h3 className="readout-name font-display font-bold">{focus.label}</h3>
              <div className="readout-fields">
                <div className="readout-field font-mono"><span>CLASS</span><span>/ {focus.sub}</span></div>
                <div className="readout-field font-mono"><span>STATE</span><span style={{ color: telemetryColor }}>{telemetry}</span></div>
                <div className="readout-field font-mono"><span>ROUTE</span><span>{focus.target}</span></div>
              </div>
              <div className="readout-desc font-mono">{focus.desc}</div>
            </div>
            <div className="readout-meter font-mono">
              <span>01</span>
              <span className="meter-track">
                <span className="meter-fill" style={{ width: `${meterPct}%` }} />
                <span className="meter-dot" style={{ left: `${meterPct}%` }} />
              </span>
              <span>09</span>
            </div>
            <span className="nav-scan" />
          </div>
        </div>

        {/* Footer */}
        <div className="nav-footer">
          {/* Progress pills — hidden on small screens to save space */}
          <div className="nav-info hidden sm:flex items-center gap-2">
            {NAV_ITEMS.map((item, i) => (
              <div
                key={item.num}
                className="rounded-full"
                style={{
                  width: activeIdx === i ? 18 : 4,
                  height: 4,
                  backgroundColor: activeIdx === i ? 'var(--color-fire)' : 'var(--color-stone)',
                  opacity: activeIdx === i ? 1 : 0.3,
                  transition: 'all 0.3s var(--ease-out-quart)',
                }}
              />
            ))}
          </div>
          {/* Dossier — the CV opens in the in-site viewer, not a raw PDF tab.
              Sits outside the 09-node index so the node numbering stays honest. */}
          <button
            type="button"
            onClick={openDossier}
            aria-haspopup="dialog"
            data-cursor-label="Dossier"
            className="nav-info nav-dossier font-mono"
          >
            <span aria-hidden style={{ width: 8, height: 9, clipPath: HEX_DOT, background: 'currentColor', flexShrink: 0 }} />
            <span>Dossier</span>
            <span className="nav-dossier-sub">/ cv</span>
          </button>

          <span className="nav-info font-mono text-[10px] tracking-[0.15em]" style={{ color: 'var(--color-fog)' }}>
            © 2021–{new Date().getFullYear()} abdorange
          </span>
        </div>
      </div>
    </>
  )
}
